import { Injectable } from '@angular/core';

import { map } from 'rxjs/operators';

import {
  ServerResponse,
  ServerResponseData,
  HttpStatusCodesEnum,
  PermissionApplicationsEnum,
  UsersService,
  PermissionsService,
  PermissionInterface,
  PermissionModel,
} from '@spa/api';

import { Helper } from '../../../shared/classes';

@Injectable()
export class UserPermissionsService {
  private userPermissions: string[];
  private portalPermissions: PermissionModel[];

  constructor(
    private readonly usersService: UsersService,
    private readonly permissionsService: PermissionsService,
  ) {
    this.userPermissions = [];
    this.portalPermissions = [];
  }

  getUserPermissionStringsAsync(): ServerResponse<string[]> {
    if (this.userPermissions && (this.userPermissions.length > 0)) {
      return Helper.createSuccessResponseObservable<string[]>(this.userPermissions);
    }

    return this.usersService.getPermissionsNames().pipe(
      map((response: ServerResponseData<string[]>) => {
        if (response.status !== HttpStatusCodesEnum.Ok) {
          return Helper.errorsHelper().createErrorResponse<string[]>(response);
        }

        this.userPermissions = response.data as string[];

        return Helper.createSuccessResponse<string[]>(this.userPermissions);
      })
    );
  }

  getPermissionsStrings(): string[] {
    return this.userPermissions;
  }

  getPortalPermissionsAsync(): ServerResponse<PermissionModel[]> {
    if (this.portalPermissions && (this.portalPermissions.length > 0)) {
      return Helper.createSuccessResponseObservable<PermissionModel[]>(this.portalPermissions);
    }

    return this.permissionsService.getAll(PermissionApplicationsEnum.FundPortal).pipe(
      map((response: ServerResponseData<PermissionInterface[]>) => {
        if (response.status !== HttpStatusCodesEnum.Ok) {
          return Helper.errorsHelper().createErrorResponse<PermissionInterface[]>(response);
        }

        const responseArrayCheck =
          Helper.errorsHelper().checkForInvalidRespondedArray<PermissionInterface>(response);

        if (!responseArrayCheck) {
          const data = response.data as PermissionInterface[];

          this.portalPermissions = data.map((permission: PermissionInterface) => {
            return new PermissionModel(permission);
          });
        }

        return Helper.createSuccessResponse<PermissionModel[]>(this.portalPermissions);
      })
    );
  }

  getPortalPermissions(): PermissionModel[] | null {
    return this.portalPermissions;
  }

  getPortalPermissionsByStrings(permissionsString: string[]): PermissionModel[] {
    return this.portalPermissions.filter((permission: PermissionModel) => {
      return (permissionsString.indexOf(permission.permissionString) >= 0);
    });
  }

  getPortalPermissionsByString(permissionString: string): PermissionModel | null {
    const filteredPermission = this.portalPermissions.filter((permission: PermissionModel) => {
      return (permissionString === permission.permissionString);
    });

    return filteredPermission.length > 0 ? filteredPermission[0] : null;
  }

  getPortalPermissionIdsByStrings(permissionsString: string[]): number[] {
    return this.portalPermissions.filter((permission: PermissionModel) => {
      return (permissionsString.indexOf(permission.permissionString) >= 0);
    }).map((permission: PermissionModel) => permission.id);
  }

  getPortalPermissionStringsByIds(ids: number[]): string[] {
    return this.portalPermissions.filter((permission: PermissionModel) => {
      return ids.indexOf(permission.id) >= 0;
    }).map((permission: PermissionModel) => permission.permissionString);
  }

  getPortalPermissionIdByString(permissionString: string): number | null {
    const filteredPermissions = this.portalPermissions.filter((permission: PermissionModel) => {
      return permission.permissionString === permissionString;
    });

    return filteredPermissions.length > 0 ? filteredPermissions[0].id : null;
  }
}
